import { SERVER_PROTOCOLS, protocolLabel, refusedOfferText } from "./protocol";
import type { StatusTone } from "./status";
import type { ConnectionAttempt } from "./types";

/**
 * A handshake, as an installer on site needs to read it.
 *
 * The server stores `authenticateUpgrade`'s reason code verbatim. The headline
 * says what went wrong in the installer's terms and the hint says what to change,
 * because the person reading this page is usually standing at the station with
 * its configuration screen open.
 */

const REJECTIONS: Readonly<Record<string, string>> = {
  bad_credentials: "Wrong password",
  missing_credentials: "No password sent",
  identity_mismatch: "Username is not the station id",
  unknown_charge_point: "Station not registered",
  charge_point_hidden: "Station is hidden",
  missing_charge_point_id: "No station id in the URL",
  auth_error: "Server could not check the password",
  no_dialect: "Server has no handler for that version",
  handshake_aborted: "Malformed WebSocket handshake",
  client_closed_early: "Station hung up before an answer",
};

export function attemptHeadline(attempt: ConnectionAttempt): string {
  switch (attempt.outcome) {
    case "accepted":
      return attempt.ocppVersion === null
        ? "Connected"
        : `Connected on ${protocolLabel(attempt.ocppVersion)}`;
    case "unsupported_protocol":
      return "No common OCPP version";
    case "rejected": {
      const reason = attempt.reason ?? "unknown";
      // A code this page has no words for is shown as sent: a new reason the
      // server learns is still visible here before anybody updates this table.
      return REJECTIONS[reason] ?? `Rejected: ${reason}`;
    }
  }
}

const MALFORMED_CREDENTIAL: Readonly<Record<string, string>> = {
  unsupported_scheme:
    "An Authorization header arrived, but not as HTTP Basic. Set the station to Basic authentication.",
  empty_credentials: "The Authorization header said Basic but carried nothing after it.",
  not_a_user_password_pair:
    "The Basic credential did not decode to id:password. Check the station is not sending the password alone.",
};

export function attemptHint(attempt: ConnectionAttempt): string | undefined {
  if (attempt.outcome === "unsupported_protocol") {
    return `Offered ${refusedOfferText(attempt.offeredSubprotocols)}; this server speaks ${SERVER_PROTOCOLS.map(protocolLabel).join(" and ")}.`;
  }
  if (attempt.outcome !== "rejected") return undefined;

  switch (attempt.reason) {
    case "missing_credentials":
      return (
        MALFORMED_CREDENTIAL[attempt.authPresented] ??
        "The station sent no Authorization header. Set its password, or its security profile to 1 (Basic)."
      );
    case "bad_credentials":
      return "The password does not match. If it was rotated, the station still holds the old one.";
    case "identity_mismatch":
      return "The Basic username has to be the same id as the last part of the URL.";
    case "unknown_charge_point":
      return "Nothing is registered under this id. Register it, or check the id in the station's URL.";
    case "charge_point_hidden":
      return "An operator hid this station, so it may not connect. Unhide it on its page to let it back in.";
    case "missing_charge_point_id":
      return `The URL has to end in the station id, like /ocpp/CP-0001. This one was ${attempt.path || "empty"}.`;
    default:
      return undefined;
  }
}

export function attemptTone(attempt: ConnectionAttempt): StatusTone {
  if (attempt.outcome !== "accepted") return "faulted";
  return attempt.closedAt === null ? "available" : "idle";
}

/**
 * How long the socket was (or has been) up, and how it ended. Only for an
 * accepted handshake: a rejection never became a connection, and a version
 * refusal is closed by the server at once, which is not worth a duration.
 */
export function connectionSpan(
  attempt: ConnectionAttempt,
  now: number = Date.now(),
): string | undefined {
  if (attempt.outcome !== "accepted") return undefined;

  const start = Date.parse(attempt.attemptedAt);
  if (attempt.closedAt === null) return `open for ${duration(now - start)}`;

  // No code is a row the server closed on its own behalf (after a restart),
  // where the reason is the whole story.
  const ended = [
    attempt.closeCode === null ? null : `closed ${attempt.closeCode}`,
    attempt.closeReason,
  ]
    .filter((part) => part !== null && part !== "")
    .join(" ");
  return `lasted ${duration(Date.parse(attempt.closedAt) - start)} · ${ended}`;
}

/**
 * What the server accepts as a station id (`domain/chargePoint/identifier.ts`).
 * Checked before the filter is sent, so a stray space is a hint under the field
 * rather than a 400 the page would repeat every two seconds.
 */
const STATION_ID = /^[A-Za-z0-9._-]{1,255}$/;

export function isStationIdFilter(value: string): boolean {
  return STATION_ID.test(value);
}

function duration(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}
